import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Chip,
    Stack,
    Alert,
    Autocomplete,
    IconButton
} from "@mui/material";
import { Download as DownloadIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import teamService from "../../services/teamService";
import { getCollectionDocuments } from "../../firebase/firestore";
import useAuth from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";

const TEAM_TYPES = ["volunteer", "event", "startup", "custom"];

export default function AdminTeamsPage() {
    const { user, userProfile } = useAuth();
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamName, setTeamName] = useState("");
    const [teamType, setTeamType] = useState("volunteer");
    const [leader, setLeader] = useState(null);
    const [members, setMembers] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");
            const [allTeams, allUsers] = await Promise.all([
                teamService.getAllTeams(),
                getCollectionDocuments("users")
            ]);
            const createdTeams = (allTeams || []).filter((t) => !!t.createdBy);
            setTeams(createdTeams);
            setUsers(allUsers || []);
        } catch (e) {
            setError(e.message || "Failed to load teams");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.uid && userProfile?.role === "admin") {
            loadData();
        }
    }, [user?.uid, userProfile?.role]);

    const handleOpenCreate = () => {
        setTeamName("");
        setTeamType("volunteer");
        setLeader(null);
        setMembers([]);
        setCreateOpen(true);
    };

    const handleCreateTeam = async () => {
        try {
            setError("");
            setSuccess("");
            if (!teamName.trim()) {
                setError("Team name is required");
                return;
            }
            if (!leader) {
                setError("Leader is required");
                return;
            }
            const memberIds = members.map((m) => m.id);
            await teamService.createAdminTeam(user.uid, {
                teamName,
                teamType,
                leaderId: leader.id,
                memberIds
            });
            setCreateOpen(false);
            setSuccess("Team created successfully");
            await loadData();
        } catch (e) {
            setError(e.message || "Failed to create team");
        }
    };

    const leadersOptions = useMemo(
        () => users.filter((u) => u.role === "student" || u.role === "startup"),
        [users]
    );
    const membersOptions = leadersOptions;

    const resolveUser = (userId) => users.find((u) => u.id === userId);

    const handleOpenDetails = (team) => {
        setSelectedTeam(team);
        setDetailOpen(true);
    };

    const handleDownloadExcel = () => {
        if (!teams || teams.length === 0) return;
        const headers = [
            "Team ID",
            "Team Name",
            "Team Type",
            "Leader Name",
            "Leader Email",
            "Leader College",
            "Members Count",
            "Member Names",
            "Member Emails"
        ];
        const rows = teams.map((team) => {
            const leaderUser = resolveUser(team.leaderId);
            const memberUsers = (team.memberIds || team.members || []).map((id) => resolveUser(id)).filter(Boolean);
            const memberNames = memberUsers.map((u) => u.displayName || u.firstName || u.email || u.id).join(" | ");
            const memberEmails = memberUsers.map((u) => u.email || "").join(" | ");
            return [
                team.id,
                team.teamName,
                team.teamType || "",
                team.leaderName || leaderUser?.displayName || leaderUser?.firstName || "",
                team.leaderEmail || leaderUser?.email || "",
                team.leaderCollege || leaderUser?.collegeName || leaderUser?.college || "",
                memberUsers.length.toString(),
                memberNames,
                memberEmails
            ];
        });
        const csvLines = [headers.join(","), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))];
        const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "teams.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <AdminLayout title="Teams Management">
            <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            Teams Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create and manage teams for volunteering, events, startups and more.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadExcel}
                            disabled={teams.length === 0}
                        >
                            Download Excel
                        </Button>
                        <Button variant="contained" onClick={handleOpenCreate}>
                            Create Team
                        </Button>
                    </Stack>
                </Box>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="error" onClose={() => setError("")}>
                            {error}
                        </Alert>
                    </Box>
                )}
                {success && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="success" onClose={() => setSuccess("")}>
                            {success}
                        </Alert>
                    </Box>
                )}

                {loading ? (
                    <Typography>Loading...</Typography>
                ) : teams.length === 0 ? (
                    <Box sx={{ textAlign: "center", mt: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                            No teams created yet.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {teams.map((team) => {
                            const leaderUser = resolveUser(team.leaderId);
                            const membersUsers = (team.memberIds || team.members || []).map((id) => resolveUser(id)).filter(Boolean);
                            return (
                                <Grid item xs={12} md={6} lg={4} key={team.id}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {team.teamName}
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleOpenDetails(team)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                                <Chip size="small" label={team.teamType || "custom"} />
                                                {team.teamCode && (
                                                    <Chip size="small" label={`Code: ${team.teamCode}`} />
                                                )}
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Leader:{" "}
                                                {team.leaderName ||
                                                    leaderUser?.displayName ||
                                                    leaderUser?.firstName ||
                                                    leaderUser?.email ||
                                                    team.leaderId}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Leader Email: {team.leaderEmail || leaderUser?.email || "-"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                College: {team.leaderCollege || leaderUser?.collegeName || leaderUser?.college || "-"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Members: {membersUsers.length}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle>Create Team</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Team Name"
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Team Type</InputLabel>
                            <Select
                                value={teamType}
                                label="Team Type"
                                onChange={(e) => setTeamType(e.target.value)}
                            >
                                {TEAM_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Autocomplete
                            options={leadersOptions}
                            getOptionLabel={(option) => option.displayName || option.firstName || option.email || option.id}
                            value={leader}
                            onChange={(_, value) => setLeader(value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Leader (Student Email / Name)" margin="normal" fullWidth />
                            )}
                            sx={{ mt: 1 }}
                        />
                        <Autocomplete
                            multiple
                            options={membersOptions}
                            getOptionLabel={(option) => option.displayName || option.firstName || option.email || option.id}
                            value={members}
                            onChange={(_, value) => setMembers(value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Members" margin="normal" fullWidth />
                            )}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCreateTeam}>
                            Create
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle>Team Details</DialogTitle>
                    <DialogContent>
                        {selectedTeam && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {selectedTeam.teamName}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                    <Chip size="small" label={selectedTeam.teamType || "custom"} />
                                    {selectedTeam.teamCode && (
                                        <Chip size="small" label={`Code: ${selectedTeam.teamCode}`} />
                                    )}
                                </Stack>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Leader: {selectedTeam.leaderName || selectedTeam.leaderId}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Leader Email: {selectedTeam.leaderEmail || "-"}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    College: {selectedTeam.leaderCollege || "-"}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                                    Members
                                </Typography>
                                {(selectedTeam.memberIds || selectedTeam.members || []).length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No members.
                                    </Typography>
                                ) : (
                                    <Stack spacing={0.5}>
                                        {(selectedTeam.memberIds || selectedTeam.members || []).map((id) => {
                                            const u = resolveUser(id);
                                            const name = u?.displayName || u?.firstName || u?.email || id;
                                            return (
                                                <Typography key={id} variant="body2">
                                                    {name} {u?.email ? `(${u.email})` : ""}
                                                </Typography>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminLayout>
    );
}
